/**
 * Processes module service (Wave 3.0 skeleton).
 *
 * Covers the 25 Processes pages. Requires the org to hold the `pureoa`
 * license. Policies come from the ADR-013 hybrid model:
 *   - flow-rules / auto-actions / exception-handlers → process_policy_rules
 *   - delegation-rules → process_delegations
 *   - overrides → process_overrides
 *
 * Wave 7.1 landed these endpoints; the engine that consumes them lands
 * later (ADR-015+, not blocking this wave).
 */

import { http } from './client';
import type { Schema } from './types';

// ---------------------------------------------------------------------------
// Types used by this module. Kept narrow — Wave 3.3 will type the rest as
// those pages migrate. Dashboard widgets (Wave 3.6) consume these.
// ---------------------------------------------------------------------------

export type ProcessTask = Schema<'ProcessTaskResponse'>;
export type ProcessInstance = Schema<'ProcessInstanceResponse'>;
export type ProcessRequest = Schema<'ProcessRequestResponse'>;
export type ProcessOverview = Schema<'ProcessOverviewResponse'>;
export type ProcessAnalytics = Schema<'ProcessAnalyticsResponse'>;

/**
 * The one place this frontend decides "is this request still waiting for a
 * decision". Mirrors `svdy-platform/app/processes/lifecycle.py`
 * `ACTIONABLE_REQUEST_STATUSES` — the set `approve_my_request` /
 * `reject_my_request` will act on. Keep the two in step; a value the backend
 * accepts but this list omits is work the UI hides.
 *
 * `pending` is legacy tolerance, not a value this product writes:
 * `sync_request_from_instance` emits in_review / completed / rejected /
 * cancelled / withdrawn / failed, and creation writes `submitted`.
 *
 * Extracted 2026-08-23 because `['submitted', 'in_review']` was inline at eight
 * call sites while the dashboard widget queried `status: 'pending'` — the
 * two-sources-of-truth failure of svdy-rules §10.5 with a ninth site that had
 * already drifted out of the set entirely.
 */
export const ACTIONABLE_REQUEST_STATUSES = [
  'submitted', 'in_review', 'pending',
] as const;

export function isActionableRequestStatus(status: string | null | undefined): boolean {
  return status != null
    && (ACTIONABLE_REQUEST_STATUSES as readonly string[]).indexOf(status) !== -1;
}

export type ProcessRole = Schema<'ProcessRoleResponse'>;
export type ProcessRoleCreate = Schema<'ProcessRoleCreate'>;
export type ProcessRoleUpdate = Schema<'ProcessRoleUpdate'>;

export type FormComponent = Schema<'FormComponentResponse'>;
export type FormComponentCreate = Schema<'FormComponentCreate'>;
export type FormComponentUpdate = Schema<'FormComponentUpdate'>;

export type ProcessNode = Schema<'ProcessNodeResponse'>;
export type ProcessNodeCreate = Schema<'ProcessNodeCreate'>;
export type ProcessNodeUpdate = Schema<'ProcessNodeUpdate'>;




// ---------------------------------------------------------------------------
// ProcessFlow — ADR-015 JSONB shape for ProcessDefinition.flow
//
// Saved round-trip contract between Designer (producer) and the future
// engine (consumer). See docs/ADR.md § ADR-015 for the full decision.
// ---------------------------------------------------------------------------

export type ProcessFlowNodeType =
  | 'start'
  | 'end'
  | 'approval'
  | 'notification'
  | 'condition'
  | 'cc';

export interface ProcessFlowNode {
  id: string;
  type: ProcessFlowNodeType;
  position: { x: number; y: number };
  data: {
    label: string;
    node_ref?: string | null;
    config?: Record<string, unknown>;
  };
  width?: number;
  height?: number;
}

export interface ProcessFlowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  type?: 'default' | 'smoothstep' | 'step' | 'straight';
  label?: string | null;
  data?: {
    /** Legacy plan-style discriminator; new Designer output uses sourceHandle. */
    on?: string | null;
    condition?: string | null;
    policy_rule_id?: string | null;
  };
}

export interface ProcessFlow {
  /** v1 marks flows emitted by the repaired Designer. Unversioned rows are legacy
   * and receive only the narrowly-defined compatibility rules in the backend. */
  contract_version?: 1;
  nodes: ProcessFlowNode[];
  edges: ProcessFlowEdge[];
  viewport?: { x: number; y: number; zoom: number };
}

/** Keys ReactFlow decorates live objects with that must NOT reach the
 *  backend (see ADR-015 D5). `selected` / `dragging` flip on every
 *  interaction and cause noise diffs; `measured` comes from the layout
 *  engine and is derived. We also drop any extra top-level keys a
 *  future ReactFlow version might add. */
const TRANSIENT_NODE_KEYS = new Set([
  'selected',
  'dragging',
  'measured',
  'resizing',
  'positionAbsolute',
  'zIndex',
  'internals',
  'computed',
]);
const TRANSIENT_EDGE_KEYS = new Set([
  'selected',
  'interactionWidth',
]);
const ALLOWED_NODE_KEYS = new Set([
  'id',
  'type',
  'position',
  'data',
  'width',
  'height',
]);
const ALLOWED_EDGE_KEYS = new Set([
  'id',
  'source',
  'target',
  'sourceHandle',
  'targetHandle',
  'type',
  'label',
  'data',
]);

/** Strip ReactFlow transient state from a flow before saving (ADR-015 D5).
 *  Idempotent — safe to call on already-sanitised data. */
export function sanitizeFlow(flow: unknown): ProcessFlow {
  if (!flow || typeof flow !== 'object') {
    return { nodes: [], edges: [] };
  }
  const f = flow as Record<string, unknown>;
  const rawNodes = Array.isArray(f.nodes) ? (f.nodes as Record<string, unknown>[]) : [];
  const rawEdges = Array.isArray(f.edges) ? (f.edges as Record<string, unknown>[]) : [];
  const nodes: ProcessFlowNode[] = rawNodes.map((n) => {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(n)) {
      if (ALLOWED_NODE_KEYS.has(k) && !TRANSIENT_NODE_KEYS.has(k)) {
        out[k] = n[k];
      }
    }
    return out as unknown as ProcessFlowNode;
  });
  const edges: ProcessFlowEdge[] = rawEdges.map((e) => {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(e)) {
      if (ALLOWED_EDGE_KEYS.has(k) && !TRANSIENT_EDGE_KEYS.has(k)) {
        out[k] = e[k];
      }
    }
    return out as unknown as ProcessFlowEdge;
  });
  const vp = f.viewport as Record<string, unknown> | undefined;
  const viewport =
    vp && typeof vp === 'object' && typeof vp.x === 'number' && typeof vp.y === 'number' && typeof vp.zoom === 'number'
      ? { x: vp.x, y: vp.y, zoom: vp.zoom }
      : undefined;
  const contractVersion = f.contract_version === 1 ? 1 : undefined;
  const base: ProcessFlow = {
    ...(contractVersion ? { contract_version: contractVersion as 1 } : {}),
    nodes,
    edges,
  };
  return viewport ? { ...base, viewport } : base;
}

export type ProcessDefinition = Schema<'ProcessDefinitionResponse'>;
export type ProcessDefinitionCreate = Schema<'ProcessDefinitionCreate'>;
export type ProcessDefinitionUpdate = Schema<'ProcessDefinitionUpdate'>;
export type ProcessDefinitionPreflight = Schema<'ProcessDefinitionPreflightResponse'>;
export type ApprovalRoutingHealth = Schema<'ApprovalRoutingHealthResponse'>;
export type ApprovalRoutingBlockedStep = Schema<'ApprovalRoutingBlockedStep'>;

export interface ProcessTaskQuery {
  status?: string;
  assigned_to?: string;
  /** One instance's steps. Added with the instance detail step list. */
  instance_id?: string;
}

export interface ProcessInstanceQuery {
  status?: string;
  limit?: number;
}

export interface ProcessRequestQuery {
  status?: string;
  requester_id?: string;
  assigned_to?: string;
}

function tasksQS(q?: ProcessTaskQuery): string {
  if (!q) return '';
  const parts: string[] = [];
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (q.instance_id) parts.push(`instance_id=${encodeURIComponent(q.instance_id)}`);
  if (q.assigned_to) parts.push(`assigned_to=${encodeURIComponent(q.assigned_to)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

function instancesQS(q?: ProcessInstanceQuery): string {
  if (!q) return '';
  const parts: string[] = [];
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (q.limit != null) parts.push(`limit=${q.limit}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

function requestsQS(q?: ProcessRequestQuery): string {
  if (!q) return '';
  const parts: string[] = [];
  if (q.status) parts.push(`status=${encodeURIComponent(q.status)}`);
  if (q.requester_id) parts.push(`requester_id=${encodeURIComponent(q.requester_id)}`);
  if (q.assigned_to) parts.push(`assigned_to=${encodeURIComponent(q.assigned_to)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const processRolesApi = {
  list: () => http.get<ProcessRole[]>('/v1/processes/roles'),
  create: (data: ProcessRoleCreate) =>
    http.post<ProcessRole>('/v1/processes/roles', data),
  update: (id: string, data: ProcessRoleUpdate) =>
    http.patch<ProcessRole>(`/v1/processes/roles/${id}`, data),
  delete: (id: string) => http.delete(`/v1/processes/roles/${id}`),
  /**
   * Who fills this seat. Its own endpoint, and allowed on SYSTEM roles where `update` is
   * refused with 403 — the seat is system-defined, the occupant is the customer's answer.
   * A whole-list PUT: the UI is a picker, so "these are the people" is the operation.
   */
  setMembers: (id: string, workerIds: string[]) =>
    http.put<ProcessRole>(`/v1/processes/roles/${id}/members`, { worker_ids: workerIds }),
};

// ---------------------------------------------------------------------------
// Form components
// ---------------------------------------------------------------------------

export const formComponentsApi = {
  list: () => http.get<FormComponent[]>('/v1/processes/form-components'),
  create: (data: FormComponentCreate) =>
    http.post<FormComponent>('/v1/processes/form-components', data),
  update: (id: string, data: FormComponentUpdate) =>
    http.patch<FormComponent>(`/v1/processes/form-components/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/processes/form-components/${id}`),
};

export type FormTemplate = Schema<'FormTemplateResponse'>;
export type FormTemplateCreate = Schema<'FormTemplateCreate'>;
export type FormTemplateUpdate = Schema<'FormTemplateUpdate'>;

export const formTemplatesApi = {
  list: () => http.get<FormTemplate[]>('/v1/processes/form-templates'),
  create: (data: FormTemplateCreate) =>
    http.post<FormTemplate>('/v1/processes/form-templates', data),
  update: (id: string, data: FormTemplateUpdate) =>
    http.patch<FormTemplate>(`/v1/processes/form-templates/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/processes/form-templates/${id}`),
};

// ---------------------------------------------------------------------------
// Nodes (process graph atoms)
// ---------------------------------------------------------------------------

export const processNodesApi = {
  list: () => http.get<ProcessNode[]>('/v1/processes/nodes'),
  create: (data: ProcessNodeCreate) =>
    http.post<ProcessNode>('/v1/processes/nodes', data),
  update: (id: string, data: ProcessNodeUpdate) =>
    http.patch<ProcessNode>(`/v1/processes/nodes/${id}`, data),
  delete: (id: string) => http.delete(`/v1/processes/nodes/${id}`),
};

// ---------------------------------------------------------------------------
// Designer (process definitions)
// ---------------------------------------------------------------------------

export const processDefinitionsApi = {
  list: () => http.get<ProcessDefinition[]>('/v1/processes/definitions'),
  get: (id: string) => http.get<ProcessDefinition>(`/v1/processes/definitions/${id}`),
  create: (data: ProcessDefinitionCreate) =>
    http.post<ProcessDefinition>('/v1/processes/definitions', data),
  // Backend route is PUT (not PATCH) — all ProcessDefinitionUpdate
  // fields are Optional so partial updates work the same as PATCH.
  update: (id: string, data: ProcessDefinitionUpdate) =>
    http.put<ProcessDefinition>(`/v1/processes/definitions/${id}`, data),
  delete: (id: string) =>
    http.delete(`/v1/processes/definitions/${id}`),
  publish: (id: string) =>
    http.post<ProcessDefinition>(`/v1/processes/definitions/${id}/publish`),
  preflight: (id: string) =>
    http.post<ProcessDefinitionPreflight>(`/v1/processes/definitions/${id}/preflight`),
  /**
   * Which ACTIVE processes cannot currently route an approval, and why.
   *
   * Org-scoped rather than per-definition on purpose: an SMB runs out of approvers through ordinary
   * operations — a flat org has no manager's manager, a seat can be emptied deliberately, and a seat
   * empties BY ITSELF when its only occupant is terminated. The admin needs one answer for the tenant,
   * before a worker's submission fails, not a per-flow check they have to think to run.
   */
  routingHealth: () =>
    http.get<ApprovalRoutingHealth>('/v1/processes/routing-health'),
  cloneVersion: (id: string) =>
    http.post<ProcessDefinition>(`/v1/processes/definitions/${id}/clone-version`),
  /** Add a catalog template to this organization as an editable draft. */
  useTemplate: (id: string) =>
    http.post<ProcessDefinition>(`/v1/processes/definitions/${id}/use-template`),
  archive: (id: string) =>
    http.post<ProcessDefinition>(`/v1/processes/definitions/${id}/archive`),
  /** Save the Designer's current flow canvas (ADR-015). Runs the
   *  `sanitizeFlow` scrub so ReactFlow transient state never reaches
   *  the backend JSONB column. */
  saveFlow: (id: string, flow: unknown) =>
    http.put<ProcessDefinition>(`/v1/processes/definitions/${id}`, {
      flow: sanitizeFlow(flow),
    }),
};

// ---------------------------------------------------------------------------
// Instances, tasks, requests (4 pages / 4 pages / 5 pages — shared endpoints
// with filters). Keep the list endpoint flexible via query string.
// ---------------------------------------------------------------------------

export const processInstancesApi = {
  list: (query?: ProcessInstanceQuery) =>
    http.get<ProcessInstance[]>(`/v1/processes/instances${instancesQS(query)}`),
  get: (id: string) =>
    http.get<ProcessInstance>(`/v1/processes/instances/${id}`),
  cancel: (id: string) =>
    http.post<ProcessInstance>(`/v1/processes/instances/${id}/cancel`),
  retry: (id: string) =>
    http.post<ProcessInstance>(`/v1/processes/instances/${id}/retry`),
  /** Remove a request that never produced a decision.
   *
   *  The server refuses with a structured 409 when the request IS part of the approval record
   *  (`process_instance_has_decision`), carries uploaded files
   *  (`process_instance_has_attachments`), or started sub-flows
   *  (`process_instance_has_subflows`). Match on `detail.code` — the three say different things
   *  and "this has approval history" shown for an attachment is a message the admin cannot act
   *  on. Note that a WITHDRAWN request can still be refused: someone may have approved a step
   *  before it was taken back, and that signature is evidence. */
  remove: (id: string) =>
    http.delete<void>(`/v1/processes/instances/${id}`),
  /** Admin reroute a stuck instance to a manually-picked approver.
   *  Atomically reassigns all open tasks on the instance to
   *  new_assignee_id and records a 'rerouted' timeline event. */
  reroute: (id: string, data: { new_assignee_id: string; reason: string }) =>
    http.post<ProcessInstance>(`/v1/processes/instances/${id}/reroute`, data),
  monitoring: () =>
    http.get<unknown>('/v1/processes/instances/monitoring'),
};

export const processTasksApi = {
  list: (query?: ProcessTaskQuery) =>
    http.get<ProcessTask[]>(`/v1/processes/tasks${tasksQS(query)}`),
  get: (id: string) =>
    http.get<ProcessTask>(`/v1/processes/tasks/${id}`),
  claim: (id: string) => http.post(`/v1/processes/tasks/${id}/claim`),
  complete: (id: string, data: { outcome: string; notes?: string | null }) =>
    http.post(`/v1/processes/tasks/${id}/complete`, data),
  reassign: (id: string, data: { assigned_to: string; reason?: string | null }) =>
    http.post(`/v1/processes/tasks/${id}/reassign`, data),
};

export const processRequestsApi = {
  list: (query?: ProcessRequestQuery) =>
    http.get<ProcessRequest[]>(
      `/v1/processes/requests${requestsQS(query)}`,
    ),
  get: (id: string) =>
    http.get<ProcessRequest>(`/v1/processes/requests/${id}`),
  /** File a request FOR a worker. `requester_id` is whose request it IS; the server records the admin
   *  as `created_by` and answers `filed_on_behalf`, so the approval history says who typed it. */
  create: (body: {
    requester_id: string; definition_id: string;
    title: string; priority?: string; data?: Record<string, unknown>;
  }) => http.post<ProcessRequest>('/v1/processes/requests', body),
  /** Which processes THIS worker may start — not the admin's own. `can_worker_initiate_definition` runs
   *  against the requester, so a picker built from the admin's list would offer refused options. */
  availableFor: (workerId: string) =>
    http.get<{ id: string; name: string; description?: string | null }[]>(
      `/v1/processes/requests/available-for/${workerId}`,
    ),
  approve: (id: string, data?: { notes?: string | null }) =>
    http.post(`/v1/processes/requests/${id}/approve`, data ?? {}),
  reject: (id: string, data: { reason: string }) =>
    http.post(`/v1/processes/requests/${id}/reject`, data),
  delegate: (id: string, data: { delegated_to: string; justification?: string | null; expires_at?: string | null }) =>
    http.post(`/v1/processes/requests/${id}/delegate`, data),
};

// ---------------------------------------------------------------------------
// Private process attachments — metadata is returned on request rows; bytes
// must come through this authenticated/org-scoped endpoint.
// ---------------------------------------------------------------------------

export const processAttachmentsApi = {
  download: (attachmentId: string) =>
    http.get<Blob>(`/v1/processes/attachments/${attachmentId}/download`, { responseType: 'blob' }),
};

// ---------------------------------------------------------------------------
// Analytics + overview
// ---------------------------------------------------------------------------

export const processAnalyticsApi = {
  overview: () => http.get<ProcessOverview>('/v1/processes/overview'),
  analytics: () => http.get<ProcessAnalytics>('/v1/processes/analytics'),
};
